import { faCalendar, faClock } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactElement } from 'react'
import { formatAmount } from '~/common/utils'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import { Card } from '~/components/Global/Blocks/Card/Card'
import { MetaItem } from '~/components/Global/Blocks/Card/MetaItem'
import { formatLocaleNullableDate } from '~/utils/date'
import { type Invoice, InvoiceStatusLabels } from '../types'
import { remainingAmount, statusColors } from './InvoiceCard.css'

export type InvoiceCardProps = {
  invoice: Invoice
  embedded?: boolean
  onEdit: (invoiceId: number) => void
}

export const InvoiceCard = ({
  invoice,
  embedded = false,
  onEdit
}: InvoiceCardProps): ReactElement => {
  const handleClick = () => {
    if (invoice.id) {
      onEdit(invoice.id)
    }
  }

  const typeName =
    typeof invoice.type === 'string' ? 'Facture' : invoice.type.name

  const createdDate = formatLocaleNullableDate(
    invoice.issuedAt ?? invoice.createdAt
  )
  const dueDate = formatLocaleNullableDate(invoice.dueDate)
  const paymentDate = formatLocaleNullableDate(invoice.paymentDate)

  const hasRemainingAmount =
    invoice.status === 'partially_paid' && Number(invoice.remainingAmount) !== 0

  const metas = [
    <MetaItem key="status" title={typeName}>
      <Badge
        title={InvoiceStatusLabels[invoice.status]}
        backgroundColor={statusColors[invoice.status]}
      />
    </MetaItem>,
    <MetaItem key="createdDate" title="Date d'émission">
      <FontAwesomeIcon icon={faCalendar} /> {createdDate}
    </MetaItem>,
    !embedded && dueDate ? (
      <MetaItem key="dueDate" title="Date d'échéance">
        <FontAwesomeIcon icon={faClock} /> {dueDate}
      </MetaItem>
    ) : (
      <span key="emptyDueDate" />
    ),
    invoice.externalRef ? (
      <MetaItem key="externalRef" title="Réf. externe">
        {invoice.externalRef}
      </MetaItem>
    ) : (
      <span key="emptyExternalRef" />
    ),
    paymentDate ? (
      <MetaItem key="paymentDate" title="Date de règlement">
        <FontAwesomeIcon icon={faCalendar} /> {paymentDate}
      </MetaItem>
    ) : (
      <span key="emptyPaymentDate" />
    ),
    <MetaItem
      key="amount"
      align="right"
      title={
        hasRemainingAmount
          ? `${formatAmount(invoice.totalAmountTTC)}€ TTC`
          : 'Total'
      }
    >
      {hasRemainingAmount ? (
        <span className={remainingAmount}>
          Reste: {formatAmount(invoice.remainingAmount)}€
        </span>
      ) : (
        `${formatAmount(invoice.totalAmountTTC)}€ TTC`
      )}
    </MetaItem>
  ]

  const orderCompanyName =
    typeof invoice.order === 'string' ? null : invoice.order?.company?.name
  const companyName = invoice.contact?.company?.name || orderCompanyName || null

  return (
    <Card
      onClick={handleClick}
      title={`Facture ${invoice.id} ${invoice.label ? `- ${invoice.label}` : ''}`}
      subtitle={companyName}
      metaData={metas}
    />
  )
}
